import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Container, Modal, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { Services } from '../../api/Services';
import ServiceItem from '../../components/list-items/ServiceItem';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { buildTree } from '../../utils';
import { BdaService } from '../../models/BdaService';
import { PagedResults } from '../../models/generics';
import AppBrowsingToolbar, { Filters } from '../../components/common/AppBrowsingToolbar';
import { EventEmitter } from '../../utils/EventEmitter';
import { Categories } from '../../api/Categories';
import { DataNode } from 'antd/es/tree';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import EntityDefinitionModal from '../../components/generics/EntityDefinitionModal';
import AppLoader from '../../components/common/AppLoader';

const DEFAULT_FILTERS: Filters = {
  'categories': { placeholder: 'Select categories', treeSelect: true, query: 'categories' },
  'owned': { checkbox: true, type: 'boolean', query: 'createdBy' },
  'tags': {
    placeholder: 'Select  tags...',
    select: true,
    query: 'tags',
    type: 'string',
    options: [],
    selectProps: {
      mode: "tags",
    }
  },
}

export default function ServicesList() {
  const [services, setServices] = useState<BdaService[]>();
  const [error, setError] = useState(false);
  const [totalServices, setTotalServices] = useState<number>();
  const [latestParams, setLatestParams] = useState<URLSearchParams>();

  const [categoriesOptions, setCategoriesOptions] = useState<DataNode[]>();

  const [service, setService] = useState<BdaService>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDefinitionModal, setShowDefinitionModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const navigate = useNavigate();

  const assetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "services"));

  useEffect(() => {
    initCategories();
    // eslint-disable-next-line
  }, []);

  const servicesFilters: Filters = useMemo(() => {
    return {
      ...DEFAULT_FILTERS,
      'search': {
        placeholder: `Search ${assetLabels.singular}...`,
        query: 'freeText',
      },
      'owned': {
        ...DEFAULT_FILTERS.owned!,
        label: `My ${assetLabels.plural}`,
      },
      'categories': {
        ...DEFAULT_FILTERS.categories!,
        treeSelectProps: {
          treeData: categoriesOptions,
          multiple: true,
        },
      },
    }
  }, [categoriesOptions, assetLabels]);

  const handleClose = () => {
    setShowDeleteModal(false);
    setService(undefined);
    setShowDefinitionModal(false);
  };

  const getServices = useCallback((queryParams?: Record<string, any>) => {
    const params = new URLSearchParams(queryParams || latestParams);
    if (queryParams) setLatestParams(params);

    Services.getServices(params)
      .then(
        ({ data: servicesPages }) => {
          setServices((servicesPages as PagedResults<BdaService>).content);
          setTotalServices((servicesPages as PagedResults<BdaService>).pageMetadata.totalElements);
        },
        (err) => {
          showToast('services-error', `Could not get ${assetLabels.plural}`, `${assetLabels.plural} Error`, 'danger');
          setError(true);
        }
      );
  }, [showToast, latestParams, assetLabels]);

  const reset = useCallback((params?: Record<string, any>) => {
    setError(false);
    setServices(undefined);
    getServices(params);
  }, [getServices]);

  useEffect(() => {
    EventEmitter.subscribe('browsing:paramsChanged', reset);
    return () => EventEmitter.unsubscribe('browsing:paramsChanged');
  }, [reset]);

  const initCategories = () => {
    const params = new URLSearchParams([['populateHierarchy', 'true']]);

    Categories.getCategories(params)
      .then(
        ({ data }) => {
          setCategoriesOptions(buildTree(data));
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('categories-error', message, `Categories Error ${status ? `(${status})` : ''}`, 'danger');
          setCategoriesOptions([]);
        }
      )
  }

  const navigateToCreation = () => navigate('/services/create');
  const navigateToDetail = useCallback((id: number) => () => navigate(`/services/${id}`), [navigate]);

  const editService = useCallback((id: number, clone?: boolean) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    navigate(`/services/${id}/edit` + (clone ? "?mode=clone" : ""));
  }, [navigate]);

  const openDeleteModal = useCallback((service: BdaService) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setService(service);
  }, []);

  const openDefinitionModal = useCallback((service: BdaService) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDefinitionModal(true);
    setService(service);
  }, []);

  const deleteService = () => {
    showLoader();

    Services.deleteService(service!.id!)
      .then(
        () => {
          showToast('service-delete-success', `${assetLabels.singular} deleted successfully.`, 'Success', 'success');
          hideLoader();
          handleClose();
          reset();
        },
        () => {
          hideLoader();
          showToast('services-error', `Could not delete ${assetLabels.singular}`, `${assetLabels.singular} Error`, 'danger');
        }
      )
  }

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    if (!services) return <AppLoader className="py-5 my-5" justify="center" size={100} />

    return services.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No {assetLabels.singular} found.</span>
        </Alert>
      )
      : (
        <Row>
          {
            services.map(service => (
              <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={service.id}>
                <ServiceItem
                  service={service}
                  onClick={navigateToDetail(service.id!)}
                  onClone={editService(service.id!, true)}
                  onEdit={editService(service.id!)}
                  onDelete={openDeleteModal(service)}
                  onShowDefinition={openDefinitionModal(service)}
                />
              </Col>
            ))
          }
        </Row>
      )
  }, [services, error, assetLabels, navigateToDetail, editService, openDeleteModal, openDefinitionModal]);

  return (
    <Container fluid="md" className="mt-3">
      <AppBrowsingToolbar
        title={`${assetLabels.plural} List`}
        actions={
          <Button size="sm" variant="success" onClick={navigateToCreation}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Register {assetLabels.singular}</span>
          </Button>
        }
        filters={servicesFilters}
        total={totalServices}
      />

      <hr />

      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {assetLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {assetLabels.singular} <b>{service?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteService}>Delete</Button>
        </Modal.Footer>
      </Modal>

      <EntityDefinitionModal open={showDefinitionModal} onClose={handleClose} entity={service} label={assetLabels.singular} />
    </Container>
  )
}
